import React, { useState } from 'react';
import { Container, Table, Button, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from '@/common/PageTitle/PageTitle';
import styles from './installer-vendor-locations.module.scss';

interface InstallerLocation {
  id: string;
  locationName: string;
  address: string;
  status: 'Shipped' | 'Arrived' | 'Installed' | 'Active';
  deviceShipped: boolean;
  trackingNumber: string;
  estimatedArrival: string;
  installationDate?: string;
  notes?: string;
}

const InstallerVendorLocations: React.FC = () => {
  const { vendorNumber } = useParams<{ vendorNumber: string }>();
  const navigate = useNavigate();
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

  // Mock data for vendor lookup
  const getVendorName = (vendorNumber: string) => {
    const vendors: { [key: string]: string } = {
      '555-200-0001': 'TechPro Installations',
      '555-200-0002': 'Audio Systems Plus',
      '555-200-0003': 'Digital Install Solutions',
      '555-200-0004': 'Smart Tech Installers',
      '555-200-0005': 'Pro Audio Installations'
    };
    return vendors[vendorNumber] || 'Unknown Vendor';
  };

  // Mock data for installer locations
  const getInstallerLocations = (vendorNumber: string): InstallerLocation[] => {
    const locationsByVendor: { [key: string]: InstallerLocation[] } = {
      '555-200-0001': [
        {
          id: '1',
          locationName: 'Harbor Foods Downtown',
          address: '123 Main Street, Downtown, NY 10001',
          status: 'Shipped',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567890',
          estimatedArrival: '2024-02-15',
          notes: 'Front entrance installation'
        },
        {
          id: '2',
          locationName: 'Harbor Foods Uptown',
          address: '456 Broadway, Uptown, NY 10002',
          status: 'Arrived',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567891',
          estimatedArrival: '2024-02-10',
          notes: 'Drive-thru installation'
        },
        {
          id: '3',
          locationName: 'Harbor Foods Mall',
          address: '789 Shopping Center Dr, Mall Area, NY 10003',
          status: 'Installed',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567892',
          estimatedArrival: '2024-02-05',
          installationDate: '2024-02-08',
          notes: 'Dining area installation'
        },
        {
          id: '4',
          locationName: 'Harbor Foods Airport',
          address: '321 Airport Blvd, Terminal 2, NY 10004',
          status: 'Active',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567893',
          estimatedArrival: '2024-01-30',
          installationDate: '2024-02-02',
          notes: 'Concourse A installation'
        }
      ],
      '555-200-0002': [
        {
          id: '5',
          locationName: 'Bluebird Diner Downtown',
          address: '654 Oak Street, Downtown, CA 90210',
          status: 'Shipped',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567894',
          estimatedArrival: '2024-02-20',
          notes: 'Main dining area'
        },
        {
          id: '6',
          locationName: 'Bluebird Diner Uptown',
          address: '987 Pine Avenue, Uptown, CA 90211',
          status: 'Arrived',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567895',
          estimatedArrival: '2024-02-12',
          notes: 'Patio area installation'
        }
      ],
      '555-200-0003': [
        {
          id: '7',
          locationName: 'Summit Diners Express',
          address: '147 Fast Food Lane, Express Area, TX 75001',
          status: 'Active',
          deviceShipped: true,
          trackingNumber: '1Z999AA1234567896',
          estimatedArrival: '2024-01-25',
          installationDate: '2024-01-28',
          notes: 'Counter installation'
        }
      ]
    };
    return locationsByVendor[vendorNumber] || [];
  };

  console.log('Vendor Number from URL:', vendorNumber); // Will be used for API calls
  const vendorName = getVendorName(vendorNumber || '');
  const installerLocations = getInstallerLocations(vendorNumber || '');

  const toggleLocationExpansion = (locationId: string) => {
    setExpandedLocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Shipped':
        return styles.statusShipped;
      case 'Arrived':
        return styles.statusArrived;
      case 'Installed':
        return styles.statusInstalled;
      case 'Active':
        return styles.statusActive;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title={`${vendorName} - Installation Locations`} />
            <Button 
              variant="outline-secondary" 
              className={styles.backButton}
              onClick={() => navigate('/installer-vendors')}
            >
              ← Back to Installer Vendors
            </Button>
          </Card.Title>

          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Installation Locations</h3>
              <div className={styles.sectionInfo}>
                <span>Total Locations: {installerLocations.length}</span>
              </div>
            </div>

            <Table hover responsive className={styles.locationsTable}>
              <thead>
                <tr>
                  <th></th>
                  <th>Location Name</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Device Shipped</th>
                  <th>Tracking Number</th>
                  <th>Estimated Arrival</th>
                  <th>Installation Date</th>
                </tr>
              </thead>
              <tbody>
                {installerLocations.map((location) => (
                  <React.Fragment key={location.id}>
                    <tr className={styles.locationRow}>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          className={styles.expandButton}
                          onClick={() => toggleLocationExpansion(location.id)}
                        >
                          {expandedLocations.has(location.id) ? '▼' : '▶'}
                        </Button>
                      </td>
                      <td>{location.locationName}</td>
                      <td>{location.address}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusBadgeClass(location.status)}`}>
                          {location.status}
                        </span>
                      </td>
                      <td>
                        <span className={location.deviceShipped ? styles.shippedYes : styles.shippedNo}>
                          {location.deviceShipped ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.trackingNumber}>
                          {location.trackingNumber}
                        </span>
                      </td>
                      <td>{location.estimatedArrival}</td>
                      <td>{location.installationDate || '-'}</td>
                    </tr>
                    {expandedLocations.has(location.id) && (
                      <tr className={styles.detailsRow}>
                        <td colSpan={8}>
                          <div className={styles.locationDetails}>
                            <div className={styles.detailsSection}>
                              <h5>Installation Details</h5>
                              <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                  <strong>Location Name:</strong> {location.locationName}
                                </div>
                                <div className={styles.detailItem}>
                                  <strong>Address:</strong> {location.address}
                                </div>
                                <div className={styles.detailItem}>
                                  <strong>Status:</strong> 
                                  <span className={`${styles.statusBadge} ${getStatusBadgeClass(location.status)}`}>
                                    {location.status}
                                  </span>
                                </div>
                                <div className={styles.detailItem}>
                                  <strong>Device Shipped:</strong> 
                                  <span className={location.deviceShipped ? styles.shippedYes : styles.shippedNo}>
                                    {location.deviceShipped ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                <div className={styles.detailItem}>
                                  <strong>Tracking Number:</strong> {location.trackingNumber}
                                </div>
                                <div className={styles.detailItem}>
                                  <strong>Estimated Arrival:</strong> {location.estimatedArrival}
                                </div>
                                {location.installationDate && (
                                  <div className={styles.detailItem}>
                                    <strong>Installation Date:</strong> {location.installationDate}
                                  </div>
                                )}
                                {location.notes && (
                                  <div className={styles.detailItem}>
                                    <strong>Installation Notes:</strong> {location.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {installerLocations.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      No installation locations found for this installer
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default InstallerVendorLocations;
